import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Typography, Space, Button } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  SisternodeOutlined,
  FireOutlined,
  MehOutlined,
  CreditCardOutlined,
  CommentOutlined,
  BulbOutlined,
  BarChartOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import { getAllHubAppProfiles } from '../../services/hubAppProfileService';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: appProfiles = [] } = useQuery(['hub-app-profiles'], getAllHubAppProfiles, { staleTime: 5 * 60 * 1000 });

  useEffect(() => {
    if (location.pathname.startsWith('/app-analytics')) {
      setOpenKeys(['/app-analytics']);
      setCollapsed(true);
      return;
    }
    setOpenKeys([]);
  }, [location.pathname]);

  const canViewAppAnalytics = (app) => {
    const required = app?.analytics?.required_permission;
    if (!app?.analytics?.enabled) return false;
    if (!required) return true;
    if (user?.role === 'admin') return true;
    return (user?.permissions || []).includes(required);
  };

  const analyticsApps = appProfiles.filter(canViewAppAnalytics);
  const analyticsMenuItem =
    analyticsApps.length > 0
      ? {
          key: '/app-analytics',
          icon: <BarChartOutlined />,
          label: 'App analytics',
          children: analyticsApps.map((app) => ({
            key: `/app-analytics/${app.id}`,
            label: app.label,
          })),
        }
      : null;

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/feature-requests',
      icon: <MehOutlined />,
      label: 'Feature Requests',
    },
    {
      key: '/whats-news',
      icon: <FireOutlined />,
      label: "What's News",
    },
    {
      key: '/partners',
      icon: <SisternodeOutlined />,
      label: 'Partners',
    },
    {
      key: '/faqs',
      icon: <QuestionCircleOutlined />,
      label: 'Faqs',
    },
    {
      key: '/tapita-credits',
      icon: <CreditCardOutlined />,
      label: 'Tapita Credits',
    },
    {
      key: '/zoho-accounts',
      icon: <CommentOutlined />,
      label: 'Zoho Accounts',
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: 'User Management',
    },
    {
      key: '/hints',
      icon: <BulbOutlined />,
      label: 'CS Hints',
    },
    {
      key: '/uploads',
      icon: <FileOutlined />,
      label: 'My Files',
    },
    ...(analyticsMenuItem ? [analyticsMenuItem] : []),
  ];

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <SettingOutlined />,
      label: 'Profile',
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout className="main-app-shell" style={{ minHeight: '100vh', height: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} theme="light" width={250}>
        <div
          style={{
            padding: '16px',
            textAlign: 'center',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Typography.Title level={4} style={{ margin: 0, color: '#1890ff' }}>
            {collapsed ? 'FR' : 'Tapita Hub'}
          </Typography.Title>
        </div>

        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys)}
          style={{ border: 'none' }}
        />
      </Sider>

      <Layout className="main-app-shell-inner">
        <Header>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
              }}
            />
          </div>

          <div className="user-info">
            <Space>
              <Text>
                Welcome back, <strong>{user?.username}</strong>
              </Text>
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
                <Avatar className="user-avatar" icon={<UserOutlined />} style={{ cursor: 'pointer' }} />
              </Dropdown>
            </Space>
          </div>
        </Header>

        <Content>
          <div className="content-wrapper">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;

